/**
 * /tools/compass?clientId=X — F-024 (2026-05-23) + follow-up.
 *
 * Compass is COACH-FILL ONLY. The coach fills the form from the macOS
 * app (Tools tab → "Open as coach" → native CompassView sheet). The
 * client sees a read-only view of the captured payload here:
 *   - No completion yet → "Your coach will set this up during your
 *     next session" placeholder.
 *   - Completion present → the same locked saved view as before,
 *     surfacing the targets + +1/-1 + 1-year vision + coach action plan.
 *
 * The editable form path is gone — there's no way to submit from the
 * client web. The route stays mounted so any direct URL navigation or
 * post-coach-completion hydration still works.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { PrintButton } from '@/components/print-button';

type DomainKey = 'energy' | 'work' | 'love';
const DOMAINS: DomainKey[] = ['energy', 'work', 'love'];

interface AxisState {
  target: number;
  addOne: string;
  avoidOne: string;
}

interface CompassState {
  energy: AxisState;
  work: AxisState;
  love: AxisState;
  oneYearVision: string;
  coachActionPlan: string;
}

const DEFAULT_AXIS: AxisState = { target: 0, addOne: '', avoidOne: '' };
const DEFAULT_STATE: CompassState = {
  energy: { ...DEFAULT_AXIS },
  work: { ...DEFAULT_AXIS },
  love: { ...DEFAULT_AXIS },
  oneYearVision: '',
  coachActionPlan: '',
};

export function CompassPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [state, setState] = useState<CompassState>(DEFAULT_STATE);
  const [hasCompletion, setHasCompletion] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'compass');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          setState({
            energy: axisFrom(p.energy) ?? DEFAULT_STATE.energy,
            work: axisFrom(p.work) ?? DEFAULT_STATE.work,
            love: axisFrom(p.love) ?? DEFAULT_STATE.love,
            oneYearVision: typeof p.oneYearVision === 'string' ? p.oneYearVision : '',
            coachActionPlan: typeof p.coachActionPlan === 'string' ? p.coachActionPlan : '',
          });
          setHasCompletion(true);
          setSavedAt(existing.completed_at ?? new Date().toISOString());
        }
      } catch {
        /* swallow — no completion yet → placeholder view */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="compass-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.compass.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.compass.errors.missingClient')}</p>
      </section>
    );
  }

  if (!hydrated) {
    return (
      <section className="max-w-3xl mx-auto px-s5 py-s7" data-testid="compass-loading">
        <p className="text-sm text-ink/70">{t('tools.compass.loading')}</p>
      </section>
    );
  }

  if (hasCompletion && savedAt) {
    return (
      <CompassSavedView
        form={state}
        coachActionPlan={state.coachActionPlan}
        savedAt={savedAt}
      />
    );
  }

  return <CompassAwaitingCoachView />;
}

function axisFrom(value: unknown): AxisState | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const target = typeof v.target === 'number' ? v.target : 0;
  return {
    target,
    addOne: typeof v.addOne === 'string' ? v.addOne : '',
    avoidOne: typeof v.avoidOne === 'string' ? v.avoidOne : '',
  };
}

/* ───────────── No-completion placeholder ───────────── */

function CompassAwaitingCoachView(): JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7" data-testid="compass-awaiting-coach">
      <header className="mb-s6 rounded-xl bg-ink text-bone p-s6 text-center">
        <p className="font-mono text-xs uppercase tracking-wider text-flame-glow">
          {t('tools.compass.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-flame mt-s2">
          {t('tools.compass.title')}
        </h1>
        <p className="mt-s2 text-sm text-bone/70 max-w-prose mx-auto">
          {t('tools.compass.description')}
        </p>
      </header>
      <article className="rounded-md border border-flame/40 bg-flame-glow/20 p-s6 text-center">
        <p className="font-mono text-xs uppercase tracking-wider text-flame-deep">
          {t('tools.compass.awaitingCoach.eyebrow')}
        </p>
        <p className="mt-s3 text-base text-ink italic">
          {t('tools.compass.awaitingCoach.body')}
        </p>
      </article>
    </section>
  );
}

/* ───────────── Read-only saved view ───────────── */

function CompassSavedView({
  form,
  coachActionPlan,
  savedAt,
}: {
  form: CompassState;
  coachActionPlan: string;
  savedAt: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7" data-testid="compass-baseline">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6 rounded-xl bg-ink text-bone p-s6 text-center">
        <p className="font-mono text-xs uppercase tracking-wider text-flame-glow">
          {t('tools.compass.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-flame mt-s2">
          {t('tools.compass.title')}
        </h1>
        <p className="mt-s2 text-sm text-bone/70 max-w-prose mx-auto">
          {t('tools.compass.baseline.subtitle')}
        </p>
      </header>

      <article
        className="mb-s5 rounded-md border border-flame/50 bg-flame-glow/30 p-s4"
        role="status"
        data-testid="compass-baseline-banner"
      >
        <p className="text-sm text-ink">
          {t('tools.compass.baseline.savedOn', { when: savedAt.slice(0, 10) })}
        </p>
        <p className="mt-s1 text-xs text-ink/80">{t('tools.compass.baseline.locked')}</p>
      </article>

      <section className="space-y-s4">
        <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
          {t('tools.compass.section.targets')}
        </h2>
        {DOMAINS.map((domain) => (
          <ReadOnlyAxisCard key={domain} domain={domain} axis={form[domain]} />
        ))}
      </section>

      {form.oneYearVision.trim().length > 0 && (
        <section className="space-y-s4 mt-s6">
          <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
            {t('tools.compass.section.vision')}
          </h2>
          <article className="rounded-md border-l-4 border-flame bg-bone-2 p-s5 shadow-sm">
            <p className="text-base text-ink italic whitespace-pre-wrap">{form.oneYearVision}</p>
          </article>
        </section>
      )}

      <section className="space-y-s4 mt-s6">
        <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
          {t('tools.compass.section.actionPlan')}
        </h2>
        <article className="rounded-md border border-flame/40 bg-flame-glow/20 p-s5 shadow-sm">
          <p className="font-mono text-xs uppercase tracking-wider text-flame-deep">
            {t('tools.compass.actionPlan.eyebrow')}
          </p>
          {coachActionPlan.trim().length > 0 ? (
            <p
              className="mt-s2 text-base text-ink whitespace-pre-wrap"
              data-testid="compass-action-plan"
            >
              {coachActionPlan}
            </p>
          ) : (
            <p className="mt-s2 text-sm text-ink/80 italic">
              {t('tools.compass.actionPlan.placeholderClient')}
            </p>
          )}
        </article>
      </section>
    </section>
  );
}

function ReadOnlyAxisCard({ domain, axis }: { domain: DomainKey; axis: AxisState }): JSX.Element {
  const { t } = useTranslation();
  return (
    <article
      className="rounded-md border-l-4 border-flame bg-bone-2 p-s5 shadow-sm"
      data-testid={`compass-axis-${domain}-readonly`}
    >
      <h3 className="font-display text-lg text-ink">{t(`tools.compass.${domain}.title`)}</h3>
      <div className="mt-s3 flex items-baseline gap-s2">
        <span className="font-display text-display-xl text-flame-deep">{axis.target}</span>
        <span className="text-sm text-gray-2">/ 10</span>
      </div>
      {(axis.addOne.trim().length > 0 || axis.avoidOne.trim().length > 0) && (
        <div className="mt-s3 grid grid-cols-1 md:grid-cols-2 gap-s3">
          {axis.addOne.trim().length > 0 && (
            <div className="rounded-md bg-flame-glow/30 p-s3">
              <p className="font-mono text-xs uppercase tracking-wider text-flame-deep">
                {t('tools.compass.addOne.label')}
              </p>
              <p className="mt-s1 text-sm text-ink whitespace-pre-wrap">{axis.addOne}</p>
            </div>
          )}
          {axis.avoidOne.trim().length > 0 && (
            <div className="rounded-md bg-bone-3 p-s3">
              <p className="font-mono text-xs uppercase tracking-wider text-ink/70">
                {t('tools.compass.avoidOne.label')}
              </p>
              <p className="mt-s1 text-sm text-ink whitespace-pre-wrap">{axis.avoidOne}</p>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

